import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import SimpleReactValidator from 'simple-react-validator';
import axios from 'axios';

// Core

// material-ui

// Icons

// Styles

// Actions

// Components
import { Alert, Box, Button, Divider, Fab, hexToRgb, Tab, Tabs, Typography } from '@mui/material';

// Others
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { validatorConfig } from 'services/Validators/Validation';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { PANELTYPE_QUANTITY } from 'config/PanelTypeConfig';
import CeilingHeaderEnum, { CeilingHeaderEnumValue } from 'enums/CeilingHeaderEnum';
import { useDefaultWalls, usePanelHeaders, usePanelTypes } from 'services/Data/WallService';
import { v4 as uuidv4 } from 'uuid';
import { useEditableInventoryItems } from 'services/Data/InventoryItemServices';
import { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import { Add, AttachFile, Remove } from '@mui/icons-material';
import { usePostCalculateCartWall } from 'services/Data/CartWallServices';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { useActiveCart, usePostCartWall } from 'services/Data/CartService';
import FrequentlyBoughtTogether from '../Components/FrequentlyBoughtTogether';
import FrequentlyBoughtTogetherTypeEnum from 'enums/FrequentlyBoughtTogetherTypeEnum';
import { useSelector } from 'react-redux';
import CartRedirectionModal from '../Components/CartRedirectionModal';
import { useHistory } from 'react-router-dom';
import { TabContext, TabPanel } from '@mui/lab';
import useImage from 'services/Hooks/useImage';
import ImagesSlideView from 'Components/ImagesSlideView';
import { FileTypeEnumValue } from 'enums/FileTypeEnum';
import InventoryItemDealerPortalTypeEnum, {
  InventoryItemDealerPortalTypeEnumValue,
} from 'enums/InventoryItemDealerPortalTypeEnum';
import { IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(theme => ({
  card: {
    border: '0',
    marginBottom: '30px',
    marginTop: '30px',
    color: 'rgba(' + hexToRgb(theme.palette?.grey?.['900']) + ', 0.87)',
    background: theme.palette?.grey?.['50'],
    width: '100%',
    boxShadow: '0 1px 4px 0 rgba(' + hexToRgb(theme.palette?.grey?.['900']) + ', 0.14)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '0',
    wordWrap: 'break-word',
    fontSize: '.875rem',
  },
  forceWhiteText: {
    color: 'white',
  },
  itemCardContent: {
    padding: 0,
  },
  itemCard: {
    borderRadius: 0,
    padding: 10,
  },
  tabsList: {
    position: 'relative',
    minHeight: 10,
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.secondary.main,
    },
    '& :hover': {
      color: theme.palette.secondary.main,

      '& >svg': {
        color: theme.palette.secondary.main,
      },
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main}`,
    },
  },
  tabItem: {
    maxWidth: 'none',
    minWidth: 10,
    minHeight: 10,
    paddingLeft: 20,
    paddingRight: 20,
    textTransform: 'none',
    color: theme.palette.common.black,
    fontSize: 18,
    fontWeight: theme.typography.fontWeightRegular,
  },
  fileLink: {
    color: theme.palette.common.black,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
}));

const Wall = props => {
  const classes = useStyles();
  const history = useHistory();

  const { t } = useTranslation();
  const forceUpdate = useForceUpdate();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const { getPanelTypeImage } = useImage();
  const { nameReference, labelReference, customLabelReference } = useLanguageReference();

  const { inventoryItemId: legacyInventoryItemId, dealerPortalMenuProductId } = useParams();
  const inventoryItemId = legacyInventoryItemId || dealerPortalMenuProductId;

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const { data: defaultWalls, isFetched: gotDefaultWalls, isLoading: gettingDefaultWall } = useDefaultWalls();
  const { data: activeCart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);
  const { mutate: addToCart } = usePostCartWall(accountReducer.isDemo);
  const { data: panelTypes, isLoading: gettingPanelType } = usePanelTypes();
  const { data: panelHeaders, isLoading: gettingPanelHeader } = usePanelHeaders();
  const {
    data: editablePanelTypeInventoryItems = [],
    isLoading: gettingEditablePanelTypeInventoryItems,
  } = useEditableInventoryItems();
  const { mutateAsync: getWallPrice } = usePostCalculateCartWall();

  const [wall, setWall] = useState();

  // eslint-disable-next-line no-unused-vars
  const [_, setDisablePanelTypeSelection] = useState(false);
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);
  const [currentTabValue, setCurrentTabValue] = useState(1);

  const panelTypePanelHeaderAssociatedInventoryItemIds = useMemo(() => {
    return panelTypes
      ?.find(pt => pt.id === wall?.panelType_Id)
      ?.panelTypeInventoryItems?.filter(ptii => ptii.panelHeader_Id === wall?.panelHeader_Id)
      .map(ii => ii.inventoryItem_Id);
  }, [panelTypes, wall?.panelHeader_Id, wall?.panelType_Id]);

  const filteredPanelTypeInventoryItems = useMemo(
    () => editablePanelTypeInventoryItems.filter(item => panelTypePanelHeaderAssociatedInventoryItemIds?.includes(item.id)),
    [editablePanelTypeInventoryItems, panelTypePanelHeaderAssociatedInventoryItemIds],
  );

  const selectedPanelType = useMemo(() => panelTypes?.find(p => p.id === wall?.panelType_Id), [
    panelTypes,
    wall?.panelType_Id,
  ]);

  const currentWallConfiguredInventoryItems = useMemo(
    () => wall?.cartWallInventoryItems?.filter(wi => !wi.displayOnly) || [],
    [wall?.cartWallInventoryItems],
  );

  const currentWallConfiguredInventoryItemIds = useMemo(
    () => currentWallConfiguredInventoryItems?.map(cwi => cwi.inventoryItem_Id),
    [currentWallConfiguredInventoryItems],
  );

  const useCustomeLabelForHeight = useMemo(() => {
    const panelTypeConfigs = PANELTYPE_QUANTITY.filter(pq => pq.name !== 'WallMountedPanels');
    const panelTypeConfigIds = panelTypeConfigs.map(ptc => ptc.id);

    const customLabelPanelTypes = panelTypes?.filter(pt => panelTypeConfigIds.includes(pt.id)).map(pt => pt.id) || [];

    return customLabelPanelTypes.includes(wall?.panelType_Id);
  }, [panelTypes, wall?.panelType_Id]);

  useEffect(() => {
    if (gotDefaultWalls) {
      const defaultWall = defaultWalls.find(d => d.panelType_Id === inventoryItemId);

      const currentWall = { ...defaultWall, id: uuidv4(), quantity: 1 };
      if (defaultWall) {
        currentWall.ceilingHeight = CeilingHeaderEnumValue.HighCeiling;
        setDisablePanelTypeSelection(true);
      }
      setWall(currentWall);

      window.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gotDefaultWalls]);

  const calculateCartWall = useCallback(
    (wall, configuredHeader) => {
      if (configuredHeader) {
        const existingConfiguredHeader = wall.cartWallInventoryItems.find(wi => wi.id === configuredHeader.id);

        if (existingConfiguredHeader) {
          const existinConfiguredHeaderIndex = wall.cartWallInventoryItems.indexOf(existingConfiguredHeader);

          wall.cartWallInventoryItems.splice(existinConfiguredHeaderIndex, 1, configuredHeader);
        } else {
          wall.cartWallInventoryItems = [...wall.cartWallInventoryItems, configuredHeader];
        }
      } else {
        wall.cartWallInventoryItems = [];
      }

      wall.cartWallInventoryItems = wall.cartWallInventoryItems.filter(cwi => cwi.displayOnly || cwi.quantity > 0);

      wall.cart_Id = '';
      wall.hasShadowBox = false;

      getWallPrice({ ...wall, images: [] }).then(rep => {
        setWall({ ...rep.data, images: wall.images, ceilingHeight: wall.ceilingHeight });
      });
    },
    [getWallPrice],
  );

  const handleUpdateWall = useCallback(
    (wall, fieldName, value) => {
      var updatedWall =
        fieldName === 'panelType_Id'
          ? { id: wall.id, quantity: wall.quantity, [fieldName]: value }
          : { ...wall, [fieldName]: value };

      if (updatedWall.panelType_Id && updatedWall.panelHeader_Id && updatedWall.numberOfPanels) {
        //setHeader({ id: uuidv4(), quantity: 1, cartWall_Id: updatedWall.id });
      }

      if (updatedWall.panelType_Id && updatedWall.numberOfPanels && updatedWall.panelHeader_Id) {
        calculateCartWall(updatedWall);
      } else {
        setWall(updatedWall);
      }
    },
    [calculateCartWall],
  );

  const handleUpdateHeader = useCallback(
    (configuredHeader, fieldName, value) => {
      const updatedHeader = configuredHeader
        ? { ...configuredHeader, [fieldName]: value, cartWall_Id: wall.id }
        : { [fieldName]: value, cartWall_Id: wall.id };

      if (!updatedHeader.id) {
        updatedHeader.id = uuidv4();
      }

      if (fieldName !== 'quantity' && !updatedHeader.quantity) {
        updatedHeader.quantity = 1;
      }

      calculateCartWall(wall, updatedHeader);
    },
    [wall, calculateCartWall],
  );

  const getAvailableQuantity = useCallback(panelTypeId => {
    return PANELTYPE_QUANTITY.find(panelType => panelType.id === panelTypeId)?.availableQuantity;
  }, []);

  const selectedPanelTypeAvailableBrandQuantity = useMemo(() => {
    if (!selectedPanelType?.id || !wall?.numberOfPanels) return 0;

    return PANELTYPE_QUANTITY.find(panelType => panelType.id === selectedPanelType.id)?.getBrandQuantity(
      wall?.numberOfPanels,
    );
  }, [selectedPanelType?.id, wall?.numberOfPanels]);

  const currentBrandQuantity = useMemo(
    () => currentWallConfiguredInventoryItems.map(cwi => Number(cwi.quantity)).reduce((acc, curr) => acc + curr, 0),
    [currentWallConfiguredInventoryItems],
  );

  const hasReachMaxBrandQuantity = useMemo(() => currentBrandQuantity >= selectedPanelTypeAvailableBrandQuantity, [
    currentBrandQuantity,
    selectedPanelTypeAvailableBrandQuantity,
  ]);

  const handleAddWall = useCallback(() => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate();
    } else {
      const panelHeader = panelHeaders.find(p => p.id === wall.panelHeader_Id);
      const cartWall = {
        id: wall.id,
        numberOfPanels: wall.numberOfPanels,
        quantity: wall.quantity,
        price: wall.price,
        subTotal: wall.subTotal,
        name: `${selectedPanelType.name} ${panelHeader[nameReference]}`,
        toolingMultiplier: 1,
        panelHeader_Id: panelHeader.id,
        panelType_Id: selectedPanelType.id,
        cart_Id: activeCart?.id,
        cartWallInventoryItems: [...wall.cartWallInventoryItems],
      };

      addToCart(cartWall);
      validator.current.hideMessages();
      setWall({ id: uuidv4() });
      setShowRedirectionModal(true);
    }
  }, [
    forceUpdate,
    panelHeaders,
    wall?.id,
    wall?.numberOfPanels,
    wall?.quantity,
    wall?.price,
    wall?.subTotal,
    wall?.cartWallInventoryItems,
    wall?.panelHeader_Id,
    selectedPanelType?.name,
    selectedPanelType?.id,
    nameReference,
    activeCart?.id,
    addToCart,
  ]);

  const isLoading = useMemo(
    () => gettingEditablePanelTypeInventoryItems || gettingPanelType || gettingPanelHeader || gettingDefaultWall,
    [gettingPanelHeader, gettingPanelType, gettingEditablePanelTypeInventoryItems, gettingDefaultWall],
  );

  const selectedPanelTypeImages = useMemo(
    () =>
      selectedPanelType?.images?.filter(
        a =>
          a.fileType === FileTypeEnumValue.Photo &&
          (!a.numberOfPanels || a.numberOfPanels === wall?.numberOfPanels) &&
          (!a.panelHeader_Id || a.panelHeader_Id === wall?.panelHeader_Id),
      ) || [],
    [selectedPanelType?.images, wall?.numberOfPanels, wall?.panelHeader_Id],
  );

  const handleDownloadFile = useCallback((fileUrl, fileName) => {
    axios
      .get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Content-Disposition': 'attachment',
        },
      })
      .then(response => {
        const href = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = href;
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(response.headers['content-disposition']);
        if (matches != null && matches[1]) {
          const originalFileName = matches[1].replace(/['"]/g, '');
          const extension = originalFileName.split('.')[1];

          link.setAttribute('download', `${fileName}.${extension}`);
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }, []);

  return (
    <Box py={3}>
      <CartRedirectionModal
        show={showRedirectionModal}
        onClose={skipRedirect => {
          setShowRedirectionModal(false);
          if (!skipRedirect) {
            const currentPathname = history.location?.pathname || '';
            const canonicalListPath = currentPathname.replace(/\/products\/[^/]+\/?$/, '');

            history.push(canonicalListPath && canonicalListPath !== currentPathname ? canonicalListPath : '/dealer/walls');
          }
        }}
        redirectLink={'/dealer/cart'}
        title={t('itemAddedToCart')}
        message={t('itemAddedToCartText')}
      />
      <GridContainer>
        <GridItem
          xs={12}
          xl={8}
          lg={7}
          md={6}
          //Was not able to refresh imageSliderView based on the selection
          key={selectedPanelTypeImages?.map(i => i.id)}>
          <ImagesSlideView
            isLoading={gettingPanelType}
            images={selectedPanelTypeImages}
            getImageUrl={imgId => getPanelTypeImage(wall?.panelType_Id, imgId)}
          />
        </GridItem>

        <GridItem xs={12} xl={4} lg={5} md={6}>
          <CmtCard>
            <CmtCardContent>
              <Box display="flex" flexDirection={'column'} style={{ width: '100%', marginBottom: '1vh' }}>
                <Typography variant="h1">{wall?.name}</Typography>
                <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
                  {
                    InventoryItemDealerPortalTypeEnum.find(e => e.value === InventoryItemDealerPortalTypeEnumValue.Walls)?.[
                      labelReference
                    ]
                  }
                </Typography>
                <Divider />
              </Box>

              <Box display="flex" flexDirection={'column'}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  <Box>
                    <Typography variant="h2">{t('price')}</Typography>
                  </Box>
                  <Typography fontSize={25} fontWeight="bold">
                    ${(wall?.subTotal ?? 0).toFixed(2)} {wall?.currencyName}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {selectedPanelType?.shortDescription && (
                  <>
                    <Typography>{selectedPanelType?.shortDescription}</Typography>

                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                <CustomAutocomplete
                  labelText={t('numberOfPanels')}
                  hasSkeleton
                  isLoading={isLoading}
                  options={getAvailableQuantity(wall?.panelType_Id) || []}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  error={validator?.current.message(t('numberOfPanels'), wall?.numberOfPanels, `required`)}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value !== wall?.numberOfPanels) {
                        handleUpdateWall(wall, 'numberOfPanels', value);
                      }
                    },
                    isOptionEqualToValue: (numberOfPanels, value) => {
                      return numberOfPanels === value;
                    },
                    value: wall?.numberOfPanels || null,
                    getOptionLabel: numberOfPanels => {
                      return numberOfPanels || '';
                    },
                    filterOptions: (numberOfPanelss, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredQuantities = numberOfPanelss.filter(numberOfPanels =>
                        searchTerms.every(searchTerm => `${numberOfPanels}`?.toLowerCase().includes(searchTerm)),
                      );

                      return filteredQuantities;
                    },
                    renderOption: (props, numberOfPanels, { inputValue }) => {
                      const result = getHighlightParts(`${numberOfPanels}`, inputValue);
                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                <CustomAutocomplete
                  labelText={t('ceilingHeight')}
                  hasSkeleton
                  isLoading={isLoading}
                  options={CeilingHeaderEnum || []}
                  formControlProps={{
                    fullWidth: true,
                  }}
                  autocompleteProps={{
                    onChange: (event, value) => {
                      if (value.value !== wall?.panelHeader_Id) {
                        handleUpdateWall(wall, 'panelHeader_Id', value.value);
                      }
                    },
                    isOptionEqualToValue: (ceilingHeight, value) => {
                      return ceilingHeight === value.value;
                    },
                    value: wall?.panelHeader_Id || null,
                    getOptionLabel: ceilingHeight => {
                      var item = CeilingHeaderEnum.find(ch => ch.value === ceilingHeight);
                      return (item && `${item[useCustomeLabelForHeight ? customLabelReference : labelReference]}`) || '';
                    },
                    filterOptions: (ceilingHeights, state) => {
                      var searchTerms = state.inputValue.toLowerCase().split(' ');
                      var filteredDiscountTypes = ceilingHeights.filter(ceilingHeight =>
                        searchTerms.every(searchTerm =>
                          ceilingHeight[useCustomeLabelForHeight ? customLabelReference : labelReference]
                            ?.toLowerCase()
                            .includes(searchTerm),
                        ),
                      );

                      return filteredDiscountTypes;
                    },
                    renderOption: (props, ceilingHeight, { inputValue }) => {
                      const result = getHighlightParts(
                        `${ceilingHeight[useCustomeLabelForHeight ? customLabelReference : labelReference]}`,
                        inputValue,
                      );
                      return (
                        <div className={classes.autoCompleteItem} {...props}>
                          {result}
                        </div>
                      );
                    },
                  }}
                />

                <Typography style={{ marginTop: '1vh' }} variant="h3">
                  {t('header')}
                </Typography>

                {currentWallConfiguredInventoryItems?.length > 0 &&
                  currentWallConfiguredInventoryItems.map(configuredHeader => (
                    <GridContainer>
                      <GridItem xs={12} md={7} sx={{ mt: 1 }}>
                        <CustomAutocomplete
                          hasSkeleton
                          options={
                            filteredPanelTypeInventoryItems
                              ?.filter(
                                ii =>
                                  ii.wallInventoryItemCategoryEnum === InventoryItemCategoryEnumValue.HeaderOptions ||
                                  ii.wallInventoryItemCategoryEnum === InventoryItemCategoryEnumValue.LowCeilingHeader,
                              )
                              .filter(ii => !currentWallConfiguredInventoryItemIds.includes(ii.id)) || []
                          }
                          formControlProps={{
                            fullWidth: true,
                          }}
                          error={
                            !hasReachMaxBrandQuantity ||
                            validator?.current.message(t('header'), configuredHeader.inventoryItem_Id, `required`)
                          }
                          autocompleteProps={{
                            onChange: (event, value) => {
                              if (value?.id !== configuredHeader?.id) {
                                handleUpdateHeader(configuredHeader, 'inventoryItem_Id', value?.id);
                              }
                            },
                            isOptionEqualToValue: (panel, value) => {
                              return panel === value;
                            },
                            value: configuredHeader?.inventoryItem_Id || null,
                            getOptionLabel: panelId => {
                              const item = filteredPanelTypeInventoryItems.find(ii => ii.id === panelId);
                              return (item && `${item.brandName}`) || '';
                            },
                            filterOptions: (panels, state) => {
                              var searchTerms = state.inputValue.toLowerCase().split(' ');
                              var filteredDiscountTypes = panels.filter(panel =>
                                searchTerms.every(searchTerm => panel.brandName?.toLowerCase().includes(searchTerm)),
                              );

                              return filteredDiscountTypes;
                            },
                            renderOption: (props, panel, { inputValue }) => {
                              const result = getHighlightParts(`${panel.brandName}`, inputValue);
                              return (
                                <div className={classes.autoCompleteItem} {...props}>
                                  {result}
                                </div>
                              );
                            },
                          }}
                        />
                      </GridItem>

                      <GridItem xs={12} md={5} sx={{ mt: 1 }}>
                        <Box
                          fontSize={20}
                          style={{ width: '15vh', marginLeft: 'auto' }}
                          display="flex"
                          justifyContent={'space-between'}
                          alignItems={'center'}>
                          <Fab
                            size="small"
                            color={'secondary'}
                            style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                            disabled={configuredHeader.quantity <= 0 || !configuredHeader.inventoryItem_Id}
                            onClick={() => {
                              handleUpdateHeader(configuredHeader, 'quantity', --configuredHeader.quantity);
                            }}>
                            <Remove />
                          </Fab>
                          <Typography>{configuredHeader.quantity ?? 0}</Typography>
                          <Fab
                            size="small"
                            color={'secondary'}
                            style={{ borderRadius: 0, width: 36, height: 36, minHeight: 36, color: '#000' }}
                            disabled={!configuredHeader.inventoryItem_Id || hasReachMaxBrandQuantity}
                            onClick={() => {
                              handleUpdateHeader(configuredHeader, 'quantity', ++configuredHeader.quantity);
                            }}>
                            <Add />
                          </Fab>
                        </Box>
                      </GridItem>
                    </GridContainer>
                  ))}

                {!hasReachMaxBrandQuantity && (
                  <GridContainer>
                    <GridItem xs={12} md={7} sx={{ mt: 1 }}>
                      <CustomAutocomplete
                        hasSkeleton
                        options={
                          filteredPanelTypeInventoryItems
                            ?.filter(
                              ii =>
                                ii.wallInventoryItemCategoryEnum === InventoryItemCategoryEnumValue.HeaderOptions ||
                                ii.wallInventoryItemCategoryEnum === InventoryItemCategoryEnumValue.LowCeilingHeader,
                            )
                            .filter(ii => !currentWallConfiguredInventoryItemIds.includes(ii.id)) || []
                        }
                        formControlProps={{
                          fullWidth: true,
                        }}
                        error={validator?.current.message(t('header'), '', `required`)}
                        autocompleteProps={{
                          onChange: (event, value) => {
                            if (value?.id) {
                              handleUpdateHeader({}, 'inventoryItem_Id', value?.id);
                            }
                          },
                          isOptionEqualToValue: (panel, value) => {
                            return panel === value;
                          },
                          value: null,
                          getOptionLabel: panelId => {
                            const item = filteredPanelTypeInventoryItems.find(ii => ii.id === panelId);
                            return (item && `${item.brandName}`) || '';
                          },
                          filterOptions: (panels, state) => {
                            var searchTerms = state.inputValue.toLowerCase().split(' ');
                            var filteredDiscountTypes = panels.filter(panel =>
                              searchTerms.every(searchTerm => panel.brandName?.toLowerCase().includes(searchTerm)),
                            );

                            return filteredDiscountTypes;
                          },
                          renderOption: (props, panel, { inputValue }) => {
                            const result = getHighlightParts(`${panel.brandName}`, inputValue);
                            return (
                              <div className={classes.autoCompleteItem} {...props}>
                                {result}
                              </div>
                            );
                          },
                        }}
                      />
                    </GridItem>
                  </GridContainer>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  mt: 2,
                }}>
                <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
                  {t('wallHeaderBrandAmountAlert', {
                    count: selectedPanelTypeAvailableBrandQuantity,
                    brandAmount: selectedPanelTypeAvailableBrandQuantity,
                  })}
                </Alert>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  display: 'flex',
                  pt: 0,
                }}>
                <Button
                  key={uuidv4()}
                  sx={{
                    borderRadius: 0,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#EFBA29',
                      boxShadow: 'none',
                    },
                  }}
                  variant={'contained'}
                  color={'secondary'}
                  onClick={handleAddWall}>
                  <Typography
                    sx={{
                      color: 'black !important',
                      fontSize: 22,
                      fontWeight: 800,
                      paddingRight: 0,
                      paddingLeft: 0,
                      paddingTop: 2,
                      paddingBottom: 2,
                      fontFamily: 'Trade Gothic Next LT Pro',
                      letterSpacing: '-0.02em',
                      mb: 1,
                    }}>
                    {t('addToCart')}
                  </Typography>
                </Button>
              </Box>
            </CmtCardContent>
          </CmtCard>

          <CmtCard sx={{ mt: 2, minHeight: '20vh' }}>
            <CmtCardContent sx={{ p: '10px !important' }}>
              <Box>
                <TabContext value={currentTabValue}>
                  <Tabs
                    className={classes.tabsList}
                    value={currentTabValue}
                    onChange={(_, value) => {
                      setCurrentTabValue(value);
                    }}
                    aria-label="profile tabs"
                    variant="scrollable"
                    allowScrollButtonsMobile>
                    <Tab className={classes.tabItem} value={1} label={t('overview')} />
                    <Tab className={classes.tabItem} value={2} label={t('installationFiles')} />
                  </Tabs>

                  <TabPanel value={1}>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingDefaultWall}
                      component={
                        <Typography>
                          <span
                            className={classes.description}
                            dangerouslySetInnerHTML={{
                              __html: selectedPanelType?.description,
                            }}
                          />
                        </Typography>
                      }
                    />
                  </TabPanel>

                  <TabPanel value={2}>
                    <Typography>{selectedPanelType?.userGuideFiles?.length > 0 ? t('filesInfo') : t('noFiles')}</Typography>
                    <SkeletonWrapper
                      skeletonProps={{
                        height: 36,
                      }}
                      fullWidth
                      isLoading={gettingDefaultWall}
                      component={
                        wall && (
                          <Box sx={{ ml: 5 }}>
                            <ul>
                              {selectedPanelType?.userGuideFiles?.map(ugf => (
                                <li>
                                  <span
                                    onClick={() =>
                                      handleDownloadFile(getPanelTypeImage(wall.panelType_Id, ugf.id), ugf[nameReference])
                                    }
                                    className={classes.fileLink}>
                                    <AttachFile color="secondary" fontSize="small" sx={{ marginBottom: '-0.3rem' }} />{' '}
                                    {ugf[nameReference]}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        )
                      }
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </CmtCardContent>
          </CmtCard>
        </GridItem>

        {wall?.panelType_Id && (
          <GridItem xs={12}>
            <FrequentlyBoughtTogether
              entityId={wall.panelType_Id}
              frequentlyBoughtTogetherType={FrequentlyBoughtTogetherTypeEnum.Wall}
            />
          </GridItem>
        )}
      </GridContainer>
    </Box>
  );
};

export default Wall;
